import { useRouter } from 'next/router';

import { Alert, Box, Heading, List, Paragraph } from '@zillow/constellation-10';

import { useGetListingDetails } from '../../../../hooks/queries/properties';
import accessUtils from '../../../../utils/accessUtils';

import {
  wizardFlexContainerStyles,
  wizardFormSectionContainerStyles,
  wizardHeadingStyles,
  wizardParagraphStyles,
} from '../../../../styles/wizard';

import PropertyDescriptionFormField from '../../../PostingPath/PropertyDescriptionFormField';
import PageWizard from '../../../Wizard/PageWizard';
import WizardTipCardContainer from '../../../WizardTipCardContainer';

import type { WizardBodyTemplateProps } from '../../../WizardStepController';

const RoomDescription = ({
  onGoToNextStep,
  onGoToPrevStep,
  onExitWizard,
  wizardTitle,
  progressStepper,
  totalSteps,
  currentStepIndex,
  ...rest
}: WizardBodyTemplateProps) => {
  const router = useRouter();
  const listingAlias = String(router.query.listingAlias || '');

  const { data: listingDetails } = useGetListingDetails(listingAlias);

  const shouldShowBrokerFeeAlert = accessUtils.shouldShowBrokerFeeAlert({
    state: listingDetails?.state || '',
  });

  return (
    <PageWizard
      {...rest}
      title=""
      wizardTitle={wizardTitle}
      progressStepper={progressStepper}
      totalSteps={totalSteps}
      currentStepIndex={currentStepIndex}
      onNextClick={onGoToNextStep || undefined}
      onBackClick={onGoToPrevStep || undefined}
      onExitTriggered={onExitWizard}
    >
      <Box css={wizardFlexContainerStyles}>
        <Box>
          <Box css={wizardFormSectionContainerStyles.raw()}>
            <Heading level={2} textStyle="heading-sm" css={wizardHeadingStyles}>
              Describe the room and shared spaces
            </Heading>

            <Paragraph css={wizardParagraphStyles}>
              Write several sentences describing the room and shared spaces, being sure to mention desirable features
              that may attract renters.
            </Paragraph>

            {shouldShowBrokerFeeAlert && (
              <Box css={{ marginBottom: '400' }}>
                <Alert tone="info">
                  <strong>MA Broker Fee Rule:</strong> Starting Aug 1, broker fees can be charged only to tenants who
                  directly hire a broker
                </Alert>
              </Box>
            )}

            <PropertyDescriptionFormField />
          </Box>
        </Box>

        <WizardTipCardContainer header="Tips for descriptions">
          <List.Root css={{ paddingLeft: '600' }}>
            <List.Item>Start with the room’s best features, like a spacious closet or private entrance.</List.Item>
            <List.Item>
              Include key details about the room. What level is it on? Does it have an attached bathroom? What type of
              flooring does it have? Help renters imagine themselves in the space.
            </List.Item>
            <List.Item>
              Avoid generic words (like “nice” and “beautiful”). Instead, use descriptive words: “spacious,” “airy,”
              “newly renovated,” etc.
            </List.Item>
          </List.Root>
        </WizardTipCardContainer>
      </Box>
    </PageWizard>
  );
};

export default RoomDescription;
